import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { BehaviorSubject, forkJoin, Observable, Subject, take, takeUntil } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { FilterService } from '../services/filter.service';
import { LocationService } from '../services/location.service';
import { PeopleService } from '../services/people.service';
import { PreviousRouteService } from '../services/previous-route.service';
import { AppConstants } from '../shared/app.constants';
import { AppPermissions } from '../shared/app.permissions';
import { DateRangeFilterComponent } from '../shared/components/date-range-filter/date-range-filter.component';
import { GeographicFilterComponent } from '../shared/components/geographic-filter/geographic-filter.component';
import { DateRangeFilterModel } from '../shared/models/date-range-filter-model';
import { GeographicFilterModel } from '../shared/models/geographic-filter-model';

@Component({
  selector: 'app-people',
  templateUrl: './people.component.html'
})
export class PeopleComponent implements OnInit, OnDestroy, AfterViewInit {
  canCreate = AppPermissions.canCreatePeople;
  canView = AppPermissions.canViewPeopleFilters;
  filterResultsCols: any[] = [
    { field: 'LastName', header: 'Last Name' },
    { field: 'FirstName', header: 'First Name' },
    { field: 'DateOfBirth', header: 'Birth Date' },
    { field: 'SSN', header: 'SSN' },
    { field: 'RelatedEvents', header: 'Related Events Based on Filters' },
    { field: 'MostRecentEventDate', header: 'Most Recent Event Date Based on Filters' }
 ];
  showFilterResults: boolean = false;
  showSearchResults: boolean = false;
  displayErrorStatus: boolean = false;
  displaySidebar: boolean = false;
  dateFilterModel: DateRangeFilterModel = null;
  geographicFilter: GeographicFilterModel = null;
  isJuvenile: boolean = false;
  loading: boolean = false;
  loadingEndRow: number = AppConstants.dbPagingDefaultRows;
  loadingStartRow: number = 1;
  loadingStartRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingStartRow);
  loadingEndRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingEndRow);
  messageTemplate: string = 'Loading people...';
  openViewPersonDetailForm: boolean = false;
  openViewPersonAddressDetails: boolean = false;
  openViewPersonAddressDetailForm: boolean = false;
  people: ServerModels.PeopleViewModel[] = null;
  personAddress: ServerModels.ENESS_AddressDTO;
  personAddressType: string;
  personAddressTypeFromRef: string;
  personAddressTypeOther: string;
  rows: number = AppConstants.defaultTableRows;
  showAllPeople: boolean = false;
  showLocationView: boolean = false;
  showEditLocationView: boolean = false;
  ssnSortValue: string;
  isStateLocalLE: boolean = false;
  hasDeletePermission: BehaviorSubject<boolean> = new BehaviorSubject(false);
  hasDeletePermission$: Observable<boolean> = this.hasDeletePermission.asObservable();
  hasPermissionToEditAddress: boolean = false;
  readonly peopleToggleStorageKey: string = 'peopleToggleState';
  readonly peopleStorageKey: string = 'peopleFilterModel';
  readonly geoFilterStorageKey: string = 'geoFilterStorageKeyPeople';
  @ViewChild(DateRangeFilterComponent, { static: false }) dateRangeFilterCmp: DateRangeFilterComponent;
  @ViewChild(GeographicFilterComponent, { static: false }) geoFilterCmp: GeographicFilterComponent;
  @ViewChild(Table, { static: false }) dataTable: Table;
  private _unsubscribe$: Subject<void> = new Subject<void>();

  private _personDTO: ServerModels.ENESS_PersonDTO = null;
  personDTO: BehaviorSubject<ServerModels.ENESS_PersonDTO> = new BehaviorSubject(this._personDTO);
  personDTO$: Observable<ServerModels.ENESS_PersonDTO> = this.personDTO.asObservable();


  constructor(private peopleService: PeopleService,
    private locationService: LocationService,
    private messageService: MessageService,
    private filterService: FilterService,
    private authService: AuthService,
    public hostElement: ElementRef,
    private previousRouteService: PreviousRouteService,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (!this.authService.hasRole(['NESS Administrator']) && this.authService.hasUserType(['StateLocalLE'])) {
      this.filterService.geographicFilterModel$.next(null);
      this.isStateLocalLE = true;
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this.messageService.clear('peopleMsgs');
    this.messageService.clear('viewPersonDetailMsg');
    this.messageService.clear('viewPersonAddressDetailMsg');
  }

  ngAfterViewInit(): void {
    const toggleState: { showSearchResults: string, showFilterResults: string } = JSON.parse(sessionStorage.getItem(this.peopleToggleStorageKey));
    if (toggleState?.showFilterResults) {
      this.applyFilters();
    } else if (toggleState?.showSearchResults) {
      this.toggleSearchGrid(true);
    } else {
      this._setFilters();
    }
    this.changeDetectorRef.detectChanges();
  }

  public toggleFilterGrid(b: boolean): void {
    this.showSearchResults = !b;
    this.showFilterResults = b;
    sessionStorage.setItem(this.peopleToggleStorageKey, JSON.stringify({ showSearchResults: !b, showFilterResults: b }));
  }

  public toggleSearchGrid(b: boolean): void {
    this.showSearchResults = b;
    this.showFilterResults = !b;
    sessionStorage.setItem(this.peopleToggleStorageKey, JSON.stringify({ showSearchResults: b, showFilterResults: !b }));
  }


  private _setFilters(): void {
    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ'])) {
      this.geoFilterCmp.applyFilters();
      let storedGeoFilterModel = JSON.parse(sessionStorage.getItem(this.geoFilterStorageKey));
      this.geographicFilter = new GeographicFilterModel(storedGeoFilterModel['_fieldDivisionID'],
        storedGeoFilterModel['_fieldDivisionGroupID'],
        storedGeoFilterModel['_fieldDivisionGroups'],
        storedGeoFilterModel['_stateID'],
        storedGeoFilterModel['_countyID'],
        storedGeoFilterModel['_counties'],
        storedGeoFilterModel['_agencyID'],
        storedGeoFilterModel['_agencies'],
        storedGeoFilterModel['_fieldDivisionName'],
        storedGeoFilterModel['_groupName'],
        storedGeoFilterModel['_groupNameList'],
        storedGeoFilterModel['_stateName'],
        storedGeoFilterModel['_countyName'],
        storedGeoFilterModel['_countyNameList'],
        storedGeoFilterModel['_agencyName'],
        storedGeoFilterModel['_agencyNamesList']
      );
    } else {
      this.filterService.geographicFilterModel$.next(null);
    }

    this.dateRangeFilterCmp.applyFilters();
    let storedDateFilter = JSON.parse(sessionStorage.getItem(this.peopleStorageKey));
    this.dateFilterModel = new DateRangeFilterModel(storedDateFilter['_fieldDivisionID'],
      storedDateFilter['_duration'],
      storedDateFilter['_startDate'],
      storedDateFilter['_endDate']);
  }



  public applyFilters(): void {
    this.toggleFilterGrid(true);
    this._setFilters();
    this.messageTemplate = 'Loading people...';
    this.people = [];
    this.showAllPeople = false;
    if (this.dataTable) {
      this.dataTable.reset();
    }
    this._clearLoadingStatus();
    this._getPeopleViewModel(0);
  }

  public resetDefaultFilters(): void {
    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ'])) {
      this.geoFilterCmp.resetToDefaultFilters();
    }
    this.dateRangeFilterCmp.resetToDefaultFilters();
  }

  public clearAppliedFilters(): void {
    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFFieldDivision', 'ATFHQ'])) {
      this.filterService.fieldDivisionName$.next(null);
      this.filterService.groupName$.next(null);
      this.filterService.groupNameList$.next(null);
      this.filterService.stateName$.next(null);
      this.filterService.countyName$.next(null);
      this.filterService.countyNameList$.next(null);
      this.filterService.agencyNames$.next(null);
      this.filterService.agencyNameList$.next(null);
    }
    this.filterService.dateRangeFilterModel$.next(null);
    this.people = [];
    this.showAllPeople = true;
    if (this.dataTable) {
      this.dataTable.reset();
    }
    this._clearLoadingStatus();
    this._getPeopleViewModel(0);
  }

  public deletePersonCompleted(data: string) {
    if (data != null) {
      let index: number = -1;
      if (this.showFilterResults) {
        index = this.people.indexOf(this.people.find(x => x.PersonID == data));
        this.people = this.people.filter((val, i) => i != index)
        // updates table in case filter is in place so deleted person does not persist
        this.dataTable.value = [...this.people];
        this.dataTable._filter();
      } else if (this.showSearchResults) {
        let peopleResults: ServerModels.ENESS_PersonDTO[] = this.peopleService.findPeopleResultSubj.getValue() ?? [];
        index = peopleResults.findIndex(x => x.PersonID == data);
        peopleResults = peopleResults.filter((val, i) => i != index);
        this.peopleService.findPeopleResultSubj.next(peopleResults);
      }
      this.displaySidebar = false;
    }
  }

  public newPerson(): void {
    this.previousRouteService.returnUrl = this.router.url;
    this.router.navigate(['/person-form']);
  }

  public ssnFilterEvent(event: KeyboardEvent) {
    let charCode = event.code ? event.code : event.key;
    if (charCode != '46' && charCode != '45' && charCode > '31' && (charCode < '48' || charCode > '57'))
      return false;

    if (this.ssnSortValue && (this.ssnSortValue.length == 3 || this.ssnSortValue.length == 6)) {
      if (charCode != '45') this.ssnSortValue = this.ssnSortValue + "-";
    }
    return true;
  }

  public viewPersonDetails(personId: string): void {
    this.displaySidebar = true;
    this.openViewPersonDetailForm = true;
    this.openViewPersonAddressDetails = false;
    this.openViewPersonAddressDetailForm = false;
    this.personAddress = null;
    this.peopleService.getPersonDetailsDTOById(personId).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (person: ServerModels.ENESS_PersonDTO) => {
        this._hasDeletePermission(personId);
        this._getPersonLECases(personId);
        this._getPersonInfoReports(personId);
        this._getPersonResults(personId);
        this._personDTO = person;
        this.personDTO.next(this._personDTO);
      },
      error: (err:any) => {
        this.messageService.addAll([{ key: 'viewPersonDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      }
    });
  }

  public viewAddressDetails(addressDetails: any[]): void {
    this.displaySidebar = true;
    this.openViewPersonDetailForm = false;
    this.openViewPersonAddressDetails = true;
    this.openViewPersonAddressDetailForm = false;
    this.hasPermissionToEditAddress = addressDetails[1];
    this.personAddressTypeFromRef = addressDetails[2];
    this.personAddressTypeOther = addressDetails[3];
    this.personAddressType = addressDetails[2] === "Other" ? addressDetails[2] + ": " + addressDetails[3] : addressDetails[2];
    forkJoin({
      address: this.locationService.getAddressDetailsById(addressDetails[0]),
      leCases: this.locationService.getAddressLECases(addressDetails[0]),
      people: this.locationService.getAddressPeople(addressDetails[0])
    }).pipe(take(1)).subscribe({
      next: (val: { address: ServerModels.ENESS_AddressDTO, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
        this.showLocationView = true;
        this.personAddress = val.address;
        this.locationService.addressLECasesSubj.next(val.leCases);
        this.locationService.addressPeopleSubj.next(val.people);
      },
      error: (err:any) => {
        this.messageService.addAll([{ key: 'viewAddressDetailsMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      }
    })
  }

  public onShowSidebar(): void {
    window.setTimeout(() => {
      let targetEl = this.hostElement.nativeElement.getElementsByClassName('ui-sidebar-active');
      if (targetEl.length) {
        targetEl[0].scrollTop = 0;
        targetEl[0].setAttribute("tabindex", "-1");
        targetEl[0].focus();
      }
    }, 1000);
  }

  public clearSidebar(): void {
    this.showEditLocationView = false;
    this.openViewPersonDetailForm = true;
    this.openViewPersonAddressDetailForm = false;
    this.personAddress = null;
  }

  public editLocation(editLocation: string): void {
    if (editLocation === 'Edit') {
      this.openViewPersonDetailForm = false;
      this.openViewPersonAddressDetails = false;
      this.openViewPersonAddressDetailForm = true;
      this.showLocationView = false;
      this.showEditLocationView = true;
    }
  }

  public onLocationFormClose(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.viewAddressDetails([newAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
  }

  public onViewPersonDetailsClose(): void {
    this._personDTO = null;
    this.personDTO.next(this._personDTO);
    this.hasDeletePermission.next(false);
    this.peopleService.personLECasesSubj.next(null);
    this.peopleService.personInfoReportsSubj.next(null);
    this.peopleService.personResultsSubj.next(null);
    this.openViewPersonDetailForm = false;
    this.displaySidebar = false;
  }

  public onClickCloseButton(): void {
    if (this.openViewPersonDetailForm) {
      this.onViewPersonDetailsClose();
    }
    if (this.openViewPersonAddressDetails) {
      if (this.personAddress && this._personDTO && this._personDTO.ENESS_XREF_PeopleAddresses.length > 0) {
        let index = this._personDTO.ENESS_XREF_PeopleAddresses.findIndex(x => x.AddressID == this.personAddress.AddressID);
        this._personDTO.ENESS_XREF_PeopleAddresses[index].ENESS_Address = this.personAddress;
      }
      this.openViewPersonAddressDetails = false;
      this.openViewPersonDetailForm = true;
    }
    if (this.openViewPersonAddressDetailForm) {
      this.viewAddressDetails([this.personAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
    }
  }

  private _getPeopleViewModel(pageNumber: number = 0): void {
    let filtersVM = <ServerModels.FiltersViewModel>{
      FieldDivisionID: null,
      FieldDivisionGroupIDs: [],
      AgencyName: null,
      AgencyIDs: [],
      StateID: null,
      CountyIDs: [],
      StartDate: this.showAllPeople ? null : this.dateFilterModel.startDate,
      EndDate: this.showAllPeople ? null : this.dateFilterModel.endDate,
      PageNumber: pageNumber,
      RowsPerPage: AppConstants.dbPagingDefaultRows
    };

    if ((this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(['ATFHQ', 'ATFFieldDivision'])) && !this.showAllPeople) {
      filtersVM.FieldDivisionID = this.geographicFilter.fieldDivisionID;
      filtersVM.StateID = this.geographicFilter.stateID;
      filtersVM.FieldDivisionGroupIDs = this.geographicFilter.fieldDivisionGroups.map(x => x.IntelligenceReportATFReferralGroupsID);
      filtersVM.CountyIDs = this.geographicFilter.counties?.map(x => x.CountyID);
      filtersVM.AgencyIDs = this.geographicFilter.agencies?.map(x => x.AgencyID);
    } else {
      filtersVM.FieldDivisionID = null;
      filtersVM.StateID = null;
      filtersVM.CountyIDs = [];
      filtersVM.AgencyIDs = [];
      filtersVM.FieldDivisionGroupIDs = [];
    }

    this.loading = true;
    this.peopleService.getPeopleViewModel(filtersVM).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (people: ServerModels.PeopleViewModel[]) => {
        this.loading = people?.length > 0;
        if (people?.length > 0) {
          let nextPage: number = pageNumber + 1;
          this.loadingStartRow = pageNumber == 0 ? 1 : (1 + this.people.length);
          this.people = this.people.concat(people);
          this.loadingEndRow = this.people.length;
          this.loadingStartRow$.next(this.loadingStartRow);
          this.loadingEndRow$.next(this.loadingEndRow);
          this.messageTemplate = `Loaded rows ${this.loadingStartRow} thru ${this.loadingEndRow}...`;
          if (this.loading = people.length == AppConstants.dbPagingDefaultRows) {
            this._getPeopleViewModel(nextPage);
          }
        }
      },
      error: (err:any) => {
        this.loading = false;
        this.people = [];
        this.messageService.add({ key: 'peopleMsgs', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message });
      }
    });
  }

  private _getPersonResults(personId: string): void {
    if (AppPermissions.canViewResults) {
      this.peopleService.getPersonResults(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personResultsSubj.next(result),
        (err:any) => {
          this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        });
    }
  }

  private _getPersonInfoReports(personId: string): void {
    if (AppPermissions.canViewIRs) {
      this.peopleService.getPersonInfoReports(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: { 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personInfoReportsSubj.next(result),
        (err:any) => {
          this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        });
    }
  }

  private _getPersonLECases(personId: string): void {
    this.peopleService.getPersonLECases(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result: ServerModels.PersonLECases[]) =>
      this.peopleService.personLECasesSubj.next(result),
      (err:any) => {
        this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      });
  }

  private _hasDeletePermission(personId: string): void {
    this.peopleService.hasDeletePermission(personId).pipe(takeUntil(this._unsubscribe$)).subscribe((result:boolean) =>
      this.hasDeletePermission.next(result),
      (err:any) => {
        this.messageService.addAll([{ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
      });
  }

  private _clearLoadingStatus(): void {
    this.loadingStartRow = 1;
    this.loadingEndRow = AppConstants.dbPagingDefaultRows;
    this.loadingStartRow$.next(this.loadingStartRow);
    this.loadingEndRow$.next(this.loadingEndRow);
  }
}
